import { FormProvider, type UseFormReturn } from 'react-hook-form'
import { type DocumentForm } from '@klear/document/wire.client'
import { UPLOAD_ACCEPT_LIST, UPLOAD_ACCEPTED_LABEL } from '@klear/upload/upload-formats'
import { FileDropZone } from '@klear/ui/components/file-drop-zone'
import { Card, CardHeader, CardTitle, CardContent } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { Badge } from '@klear/ui/components/badge'
import FormInput from '@klear/ui/form/input'
import FormTextarea from '@klear/ui/form/textarea'
import { Label } from '@klear/ui/components/label'
import { Switch } from '@klear/ui/components/switch'
import { FileText, Upload } from 'lucide-react'
import isEmpty from 'lodash/isEmpty'
import { PeriodEndFields } from '@/app/document/period-end-fields'

interface DocumentFormProps {
  form: UseFormReturn<DocumentForm>
  onSubmit: (data: DocumentForm) => Promise<void>
  onCancel: () => void
  isEditMode?: boolean
  isSubmitting: boolean
  currentUrl?: string | null
  onDelete?: () => void
  deleteDisabled?: boolean
  showFinancialToggle?: boolean
}

const TraderDocumentForm = ({
  form,
  onSubmit,
  onCancel,
  isEditMode = false,
  isSubmitting = false,
  currentUrl,
  onDelete,
  deleteDisabled = false,
  showFinancialToggle = false
}: DocumentFormProps) => {
  const formErrors = form.formState.errors
  const hasErrors = !isEmpty(formErrors)

  const files = form.watch('file') ?? []
  const isFinancialStatement = form.watch('financialStatement') === true

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
          </Alert>
        )}

        {hasErrors && !form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Please fix the following errors:</p>
                <ul className="list-disc list-inside space-y-1">
                  {formErrors.title && <li>Title: {formErrors.title.message}</li>}
                  {formErrors.url && <li>Document URL: {formErrors.url.message}</li>}
                  {formErrors.notes && <li>Notes: {formErrors.notes.message}</li>}
                  {formErrors.file && <li>Files: {formErrors.file.message}</li>}
                  {formErrors.periodEndQuarter && <li>Period ending: {formErrors.periodEndQuarter.message}</li>}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditMode ? (
              <FormInput
                label="Document Title *"
                name="title"
                placeholder="Enter document title"
                helperText="A descriptive title for your document"
              />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="file">Files *</Label>
                <FileDropZone
                  id="file"
                  files={files}
                  onFilesChange={(next) => form.setValue('file', next, { shouldValidate: form.formState.isSubmitted })}
                  accept={UPLOAD_ACCEPT_LIST}
                  acceptedLabel={UPLOAD_ACCEPTED_LABEL}
                  disabled={isSubmitting}
                />
                {formErrors.file && <p className="text-sm text-destructive">{formErrors.file.message}</p>}
              </div>
            )}
            {isEditMode && currentUrl && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Document</label>
                <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="secondary" className="flex-1 justify-start">
                    {currentUrl.split('/').pop()}
                  </Badge>
                </div>
              </div>
            )}

            {showFinancialToggle && (
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="financial-statement">Financial statement</Label>
                  <p className="text-sm text-muted-foreground">A P&amp;L, balance sheet or other financials.</p>
                </div>
                <Switch
                  id="financial-statement"
                  checked={isFinancialStatement}
                  onCheckedChange={(checked) =>
                    form.setValue('financialStatement', checked, { shouldDirty: true, shouldValidate: false })
                  }
                />
              </div>
            )}
            {isFinancialStatement && <PeriodEndFields />}
            <FormTextarea
              label="Notes"
              name="notes"
              placeholder="Add any additional notes about this document (optional)"
              rows={3}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-2">
          {onDelete !== undefined && (
            <Button
              type="button"
              variant="outline"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={onDelete}
              disabled={isSubmitting || deleteDisabled}
            >
              Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {isEditMode ? 'Updating...' : 'Uploading...'}
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {isEditMode ? 'Update Document' : 'Upload Document'}
              </>
            )}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default TraderDocumentForm
